import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useEvents from "../../api/events";
import { useTheme } from "../../context/ThemeContext";
import RegistrationExportDialog, {
  buildExportColumns,
} from "./events/RegistrationExportDialog";
import {
  ArrowLeft,
  Download,
  Search,
  Calendar,
  Mail,
  User,
  FileText,
  Trash2,
  X,
  AlertTriangle,
  CreditCard,
  CheckCircle2,
  Clock3,
  Ban,
  Loader2,
} from "lucide-react";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

// Status presentation. Keys match backend registration.status values.
const STATUS_META = {
  paid: { label: "Betaald", icon: CheckCircle2, tone: "emerald" },
  confirmed: { label: "Bevestigd", icon: CheckCircle2, tone: "emerald" },
  pending_payment: { label: "Wacht op betaling", icon: Clock3, tone: "amber" },
  cancelled: { label: "Geannuleerd", icon: Ban, tone: "gray" },
  failed: { label: "Mislukt", icon: Ban, tone: "red" },
};

const toneClasses = (tone, isDark) => {
  const map = {
    emerald: isDark
      ? "bg-emerald-500/15 text-emerald-300"
      : "bg-emerald-50 text-emerald-600",
    amber: isDark ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-600",
    gray: isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500",
    red: isDark ? "bg-red-500/15 text-red-300" : "bg-red-50 text-red-600",
  };
  return map[tone] || map.gray;
};

const StatusBadge = ({ status, isDark }) => {
  const meta = STATUS_META[status] || STATUS_META.confirmed;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${toneClasses(meta.tone, isDark)}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {meta.label}
    </span>
  );
};

// Registraties met payment_timing "later" staan als status "confirmed" —
// zonder dit label is niet te zien dat er nog geïnd moet worden.
const PaymentTimingBadge = ({ timing, isDark }) => {
  if (timing !== "later") return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${toneClasses("amber", isDark)}`}
    >
      <Clock3 className="w-2.5 h-2.5" />
      Later te betalen
    </span>
  );
};

const EventRegistrations = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const {
    getRegistrations,
    getEventById,
    deleteRegistration,
    updateRegistrationStatus,
  } = useEvents();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [registrations, setRegistrations] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Inschrijving die op "betaald" gezet wordt; de bevestiging vraagt eerst of
  // de deelnemer daarover een mail moet krijgen.
  const [payConfirm, setPayConfirm] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [regData, eventData] = await Promise.all([
          getRegistrations(eventId),
          getEventById(eventId),
        ]);

        const parsedRegs = regData.map((r) => ({
          ...r,
          data: typeof r.data === "string" ? JSON.parse(r.data) : r.data,
        }));

        setRegistrations(parsedRegs);
        setEvent(eventData);
      } catch (error) {
        console.error("Error loading registrations:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [eventId, getRegistrations, getEventById]);

  const initiateDelete = (e, id) => {
    e.stopPropagation();
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    // De guard vangt een tweede klik op voor de knop uitgeschakeld is.
    if (!deleteId || deleting) return;

    setDeleting(true);
    try {
      await deleteRegistration(deleteId);
      setRegistrations((prev) => prev.filter((r) => r.id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      console.error(err);
      alert("Er is iets misgegaan bij het verwijderen.");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (regId, newStatus, notify = false) => {
    try {
      const updated = await updateRegistrationStatus(regId, newStatus, notify);
      setRegistrations((prev) =>
        prev.map((r) =>
          r.id === regId
            ? { ...r, status: updated?.status || newStatus, paid_at: updated?.paid_at ?? r.paid_at }
            : r,
        ),
      );
      setSelectedRegistration((prev) =>
        prev && prev.id === regId
          ? { ...prev, status: updated?.status || newStatus }
          : prev,
      );
      // De status staat goed, maar de mail vertrok niet: dat mag niet
      // stilzwijgend gebeuren, anders denkt de club dat de deelnemer verwittigd is.
      if (notify && updated?.mail_sent === false) {
        alert(
          "De status staat op betaald, maar de bevestigingsmail kon niet verstuurd worden. Verwittig de deelnemer zelf even.",
        );
      }
    } catch (err) {
      console.error(err);
      alert("Status bijwerken mislukt.");
    }
  };

  // "Markeer als betaald" gaat langs een bevestiging, want dit is het moment
  // waarop de deelnemer zijn bevestigingsmail krijgt (of net niet).
  const confirmMarkPaid = async (notify) => {
    if (!payConfirm || markingPaid) return;
    setMarkingPaid(true);
    try {
      await handleStatusChange(payConfirm.id, "paid", notify);
      setPayConfirm(null);
    } finally {
      setMarkingPaid(false);
    }
  };

  const isPaidEvent = !!event?.payment_required;

  // Alle kolommen die in deze inschrijvingen voorkomen, in de standaardvolgorde.
  // useMemo houdt de identiteit stabiel, zodat de dialoog de bewaarde keuze niet
  // bij elke render opnieuw inleest.
  const exportColumns = useMemo(
    () =>
      buildExportColumns(
        registrations,
        (status) => (STATUS_META[status] || STATUS_META.confirmed).label,
      ),
    [registrations],
  );

  const handleExport = (columns) => {
    if (!registrations.length || !columns.length) return;

    const helpers = {
      formatDate: (value) => format(new Date(value), "dd/MM/yyyy HH:mm:ss"),
    };
    const header = columns.map((c) => c.label);
    const rows = registrations.map((reg) => {
      const row = {};
      for (const column of columns) row[column.label] = column.get(reg, helpers);
      return row;
    });

    // header meegeven i.p.v. het op de eerste rij laten raden: dat is precies
    // waardoor kolommen vroeger door elkaar liepen.
    const ws = XLSX.utils.json_to_sheet(rows, { header });
    ws["!cols"] = header.map((label) => {
      const longest = rows.reduce((max, row) => {
        const value = row[label];
        return Math.max(max, value == null ? 0 : String(value).length);
      }, label.length);
      return { wch: Math.min(Math.max(longest + 2, 10), 40) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inschrijvingen");
    XLSX.writeFile(wb, `Inschrijvingen-${event?.name || eventId}.xlsx`);
    setShowExport(false);
  };

  const filteredRegistrations = registrations.filter((reg) => {
    const searchString = searchTerm.toLowerCase();
    const dataString = JSON.stringify(reg.data).toLowerCase();
    const dateString = reg.created_at.toLowerCase();
    return (
      dataString.includes(searchString) || dateString.includes(searchString)
    );
  });

  const getField = (data, keys) => {
    for (const key of keys) {
      const foundKey = Object.keys(data).find(
        (k) => k.toLowerCase() === key.toLowerCase(),
      );
      if (foundKey) return data[foundKey];
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard/events")}
            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1
              className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {event ? event.name : "Laden..."}
            </h1>
            <p
              className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              {loading
                ? "Gegevens ophalen..."
                : `${registrations.length} inschrijving${registrations.length !== 1 ? "en" : ""}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowExport(true)}
          disabled={!registrations.length}
          className={`h-9 px-4 rounded-lg text-[13px] font-medium flex items-center gap-2 border transition-colors disabled:opacity-40 ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
        >
          <Download className="h-3.5 w-3.5" />
          Export to Excel
        </button>
      </div>

      {/* Search */}
      <div
        className={`rounded-xl p-4 border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="w-full md:flex-1 max-w-md relative">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <input
              type="text"
              placeholder="Zoeken op naam, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
            />
          </div>
          <div
            className={`flex items-center gap-1.5 text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            <User className="w-3.5 h-3.5" />
            {filteredRegistrations.length} resultaten
          </div>
        </div>
      </div>

      {/* Registrations List */}
      <div
        className={`rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div
              className={`animate-spin rounded-full h-6 w-6 border-2 ${isDark ? "border-white/20 border-t-white" : "border-gray-200 border-t-gray-900"}`}
            ></div>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div
              className={`h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50"}`}
            >
              <FileText
                className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-300"}`}
              />
            </div>
            <h3
              className={`text-[13px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              {searchTerm ? "Geen resultaten" : "Geen inschrijvingen"}
            </h3>
            <p
              className={`text-[12px] max-w-xs mx-auto ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              {searchTerm
                ? "Geen resultaten voor je zoekopdracht."
                : "Er zijn nog geen inschrijvingen voor dit evenement."}
            </p>
          </div>
        ) : (
          <>
            {/* Table header - desktop */}
            <div
              className={`hidden md:grid grid-cols-[160px_1fr_1fr_1fr_40px] gap-3 px-4 py-2.5 border-b text-[11px] font-semibold uppercase tracking-wider ${isDark ? "border-white/[0.06] text-gray-600" : "border-gray-100 text-gray-400"}`}
            >
              <span>Datum</span>
              <span>Deelnemer</span>
              <span>Contact</span>
              <span>Info</span>
              <span></span>
            </div>
            <div
              className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-50"}`}
            >
              {filteredRegistrations.map((reg, i) => {
                const firstName =
                  getField(reg.data, ["voornaam", "firstname", "first_name"]) ||
                  "";
                const lastName =
                  getField(reg.data, ["naam", "lastname", "last_name"]) ||
                  "Onbekend";
                const email =
                  getField(reg.data, [
                    "email",
                    "mail",
                    "mailadres",
                    "e-mail",
                  ]) || "-";
                const fullName = `${firstName} ${lastName}`.trim();

                const usedKeys = [
                  "voornaam",
                  "firstname",
                  "first_name",
                  "naam",
                  "lastname",
                  "last_name",
                  "email",
                  "mail",
                  "mailadres",
                  "e-mail",
                ];
                const otherEntries = Object.entries(reg.data).filter(
                  ([key]) =>
                    !usedKeys.includes(key.toLowerCase()) &&
                    typeof key === "string",
                );

                return (
                  <motion.div
                    key={reg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    onClick={() => setSelectedRegistration(reg)}
                    className={`p-4 cursor-pointer transition-colors ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50/50"}`}
                  >
                    <div className="md:grid md:grid-cols-[160px_1fr_1fr_1fr_40px] md:gap-3 md:items-center flex flex-col gap-2">
                      {/* Date */}
                      <div
                        className={`flex items-center gap-1.5 text-[12px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Calendar className="w-3 h-3" />
                        {format(new Date(reg.created_at), "dd/MM/yyyy HH:mm", {
                          locale: nl,
                        })}
                      </div>

                      {/* Name */}
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                        >
                          <User
                            className={`w-3 h-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          />
                        </div>
                        <span
                          className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {fullName || "Naamloos"}
                        </span>
                        {(isPaidEvent || reg.status === "paid" || reg.amount != null) && (
                          <StatusBadge status={reg.status} isDark={isDark} />
                        )}
                        <PaymentTimingBadge
                          timing={reg.payment_timing}
                          isDark={isDark}
                        />
                        {reg.amount != null && (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                          >
                            <CreditCard className="w-2.5 h-2.5" />€
                            {Number(reg.amount)}
                          </span>
                        )}
                      </div>

                      {/* Email */}
                      <div
                        className={`flex items-center gap-1.5 text-[12px] truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        {email}
                      </div>

                      {/* Extra info */}
                      <div className="flex flex-wrap gap-1">
                        {otherEntries.slice(0, 3).map(([key, val]) => (
                          <span
                            key={key}
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md truncate max-w-[120px] ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"}`}
                          >
                            {key}: {val}
                          </span>
                        ))}
                        {otherEntries.length > 3 && (
                          <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${isDark ? "bg-white/[0.04] text-gray-500" : "bg-gray-50 text-gray-400"}`}
                          >
                            +{otherEntries.length - 3}
                          </span>
                        )}
                      </div>

                      {/* Delete */}
                      <div className="flex justify-end">
                        <button
                          onClick={(e) => initiateDelete(e, reg.id)}
                          className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedRegistration(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative w-full max-w-lg rounded-2xl border overflow-hidden ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
          >
            {/* Modal header */}
            <div
              className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <div>
                <h3
                  className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Details Inschrijving
                </h3>
                <p
                  className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Ontvangen op{" "}
                  {format(
                    new Date(selectedRegistration.created_at),
                    "d MMMM yyyy 'om' HH:mm",
                    { locale: nl },
                  )}
                </p>
              </div>
              <button
                onClick={() => setSelectedRegistration(null)}
                className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal body */}
            <div className="max-h-[60vh] overflow-y-auto p-5 space-y-3">
              {Object.entries(selectedRegistration.data).map(([key, value]) => (
                <div
                  key={key}
                  className={`flex items-start gap-3 pb-3 border-b last:border-0 last:pb-0 ${isDark ? "border-white/[0.04]" : "border-gray-50"}`}
                >
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider w-28 pt-0.5 flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {key.replace(/_/g, " ")}
                  </span>
                  <span
                    className={`text-[13px] font-medium break-words ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {typeof value === "boolean"
                      ? value
                        ? "Ja"
                        : "Nee"
                      : typeof value === "object"
                        ? JSON.stringify(value)
                        : value || "-"}
                  </span>
                </div>
              ))}
            </div>

            {/* Payment / status management */}
            {(isPaidEvent ||
              selectedRegistration.amount != null ||
              selectedRegistration.status === "paid") && (
              <div
                className={`px-5 py-4 border-t space-y-3 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Status
                  </span>
                  <div className="flex items-center gap-2">
                    {selectedRegistration.is_member != null && (
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"}`}
                      >
                        {selectedRegistration.is_member ? "Lid" : "Niet-lid"}
                      </span>
                    )}
                    {selectedRegistration.amount != null && (
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                      >
                        € {Number(selectedRegistration.amount)}
                      </span>
                    )}
                    <PaymentTimingBadge
                      timing={selectedRegistration.payment_timing}
                      isDark={isDark}
                    />
                    <StatusBadge
                      status={selectedRegistration.status}
                      isDark={isDark}
                    />
                  </div>
                </div>
                {selectedRegistration.payment_timing === "later" && (
                  <p
                    className={`text-[11px] ${isDark ? "text-amber-300/80" : "text-amber-700"}`}
                  >
                    Deelnemer koos ervoor later te betalen (bij het
                    planningsmoment) — niet online via Mollie betaald. Markeer
                    hieronder als betaald zodra het bedrag binnen is.
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: "paid", label: "Markeer als betaald" },
                    { value: "pending_payment", label: "Wacht op betaling" },
                    { value: "cancelled", label: "Annuleer" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      disabled={selectedRegistration.status === opt.value}
                      onClick={() =>
                        opt.value === "paid"
                          ? setPayConfirm(selectedRegistration)
                          : handleStatusChange(
                              selectedRegistration.id,
                              opt.value,
                            )
                      }
                      className={`h-7 px-2.5 rounded-lg text-[11px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04] hover:text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modal footer */}
            <div
              className={`flex justify-end px-5 py-3 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <button
                onClick={() => setSelectedRegistration(null)}
                className={`h-8 px-4 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Sluiten
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showExport && (
        <RegistrationExportDialog
          isDark={isDark}
          columns={exportColumns}
          eventId={eventId}
          count={registrations.length}
          onExport={handleExport}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Markeer als betaald — met of zonder bevestigingsmail */}
      {payConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !markingPaid && setPayConfirm(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative w-full max-w-sm rounded-2xl border p-6 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
          >
            <div
              className={`h-10 w-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-emerald-500/10" : "bg-emerald-50"}`}
            >
              <CheckCircle2
                className={`h-5 w-5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}
              />
            </div>
            <h3
              className={`text-[14px] font-semibold text-center mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Markeren als betaald
            </h3>
            <p
              className={`text-[12px] text-center mb-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              {(() => {
                const email =
                  payConfirm.contact_email ||
                  payConfirm.data?.email ||
                  payConfirm.data?.Email ||
                  null;
                if (!email) {
                  return "Er staat geen e-mailadres bij deze inschrijving, dus er kan geen bevestiging verstuurd worden.";
                }
                // Wie "later betalen" koos, kreeg bij het inschrijven al een
                // bevestiging; deze mail is dan een betalingsbewijs.
                return payConfirm.payment_timing === "later"
                  ? `Stuur een bevestiging van de betaling naar ${email}? De inschrijving zelf werd al bevestigd bij het inschrijven.`
                  : `Stuur de bevestigingsmail naar ${email}? Bij een online betaling die is blijven hangen, heeft de deelnemer nog niets ontvangen.`;
              })()}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => confirmMarkPaid(true)}
                disabled={markingPaid}
                className="h-9 rounded-lg text-[13px] font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:cursor-not-allowed disabled:hover:bg-emerald-600 flex items-center justify-center gap-2"
              >
                {markingPaid ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Bezig…
                  </>
                ) : (
                  <>
                    <Mail className="h-3.5 w-3.5" />
                    Betaald + bevestiging sturen
                  </>
                )}
              </button>
              <button
                onClick={() => confirmMarkPaid(false)}
                disabled={markingPaid}
                className={`h-9 rounded-lg text-[13px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
              >
                Enkel de status wijzigen
              </button>
              <button
                onClick={() => setPayConfirm(null)}
                disabled={markingPaid}
                className={`h-8 rounded-lg text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
              >
                Annuleren
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteId(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative w-full max-w-sm rounded-2xl border p-6 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
          >
            <div
              className={`h-10 w-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-red-500/10" : "bg-red-50"}`}
            >
              <AlertTriangle
                className={`h-5 w-5 ${isDark ? "text-red-400" : "text-red-500"}`}
              />
            </div>
            <h3
              className={`text-[14px] font-semibold text-center mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Ben je zeker?
            </h3>
            <p
              className={`text-[12px] text-center mb-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Deze actie kan niet ongedaan worden gemaakt. De gebruiker ontvangt
              een bevestigingsmail.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className={`flex-1 h-9 rounded-lg text-[13px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 h-9 rounded-lg text-[13px] font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:cursor-not-allowed disabled:hover:bg-red-600 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Bezig met verwijderen…
                  </>
                ) : (
                  "Verwijderen"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EventRegistrations;
